/**
 * ReceiptScreen component displays a list of receipts with options to edit, delete, print, and view details.
 *
 * @component
 * @example
 * return (
 *   <ReceiptScreen />
 * )
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @description
 * This component fetches and displays the user's receipts. It provides functionalities to edit, delete, print (single and bulk),
 * and view receipt details. It also handles pagination and displays alerts based on user actions.
 *
 * @requires react
 * @requires react-redux
 * @requires react-bootstrap
 * @requires react-router-dom
 * @requires ../actions/receiptActions.js
 * @requires ../components/Loader
 * @requires ../components/Paginate.js
 * @requires ../constants/receiptConstants
 *
 * @function
 * @name ReceiptScreen
 *
 * @property {boolean} selected - State to track if a receipt is selected.
 * @property {object} alert - State to manage alert visibility, message, and variant.
 * @property {boolean} alert.show - Indicates if the alert is visible.
 * @property {string} alert.message - The alert message.
 * @property {string} alert.variant - The alert variant (e.g., 'success', 'info').
 * @property {string} keyword - URL parameter for search keyword.
 * @property {number} pageNumber - URL parameter for the current page number.
 *
 * @function showAlert
 * @description Displays an alert with a specified message and variant for a given duration.
 * @param {string} message - The alert message.
 * @param {string} [variant='success'] - The alert variant.
 * @param {number} seconds - Duration in milliseconds for which the alert is displayed.
 *
 * @function deleteHandler
 * @description Handles the deletion of a receipt.
 * @param {string} rimgid - The ID of the receipt to be deleted.
 *
 * @function editHandler
 * @description Handles the editing of a receipt.
 * @param {string} id - The ID of the receipt to be edited.
 *
 * @function printHandler
 * @description Opens a print preview for a single receipt with formatted layout and styling.
 * @param {object} receipt - The receipt object to print.
 *
 * @function printMultiHandler
 * @description Opens a print preview for multiple selected receipts with page breaks.
 *
 * @function deleteMultiHandler
 * @description Handles the deletion of multiple selected receipts.
 *
 * @function useEffect
 * @description Fetches receipts and handles navigation based on user login status and receipt count.
 * @param {function} dispatch - Redux dispatch function.
 * @param {function} navigate - React Router navigate function.
 * @param {object} userInfo - User information from Redux state.
 * @param {boolean} success - Indicates if the receipt deletion was successful.
 * @param {string} keyword - Search keyword.
 * @param {number} countRecpt - Count of receipts.
 * @param {number} pageNumber - Current page number.
 *
 * @function useEffect
 * @description Displays an alert if no receipts are found and navigates to the receipts page.
 * @param {function} navigate - React Router navigate function.
 * @param {number} count - Count of receipts.
 * @param {object} alert - Alert state.
 */
import React, { useEffect, useState, useContext, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Container, Card, Button, Alert, Form } from 'react-bootstrap'
import { useNavigate, useParams, Link } from 'react-router-dom'
import Loader from '../components/Loader.js'

import {
  getMyReceipts,
  deleteReceipt,
  getReceipt
} from '../actions/receiptActions.js'
import Paginate from '../components/Paginate.js'
import { API_URL } from '../apiUrl.js'

import { CATEGORY_RECEIPTS_RESET } from '../constants/receiptConstants.js'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrash, faPrint } from '@fortawesome/free-solid-svg-icons'
import { ThemeContext } from '../components/ThemeContext.js'

const ReceiptScreen = () => {
  const { theme } = useContext(ThemeContext)
  const selectedReceiptIdsRef = useRef(new Set())
  const [selectedCount, setSelectedCount] = useState(0)
  const [printMode, setPrintMode] = useState('compact')
  const [imageErrors, setImageErrors] = useState({})
  const [notfound, setNotfound] = useState(false)
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'success'
  })

  const { keyword } = useParams() || ''
  const { pageNumber } = useParams() || 1

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const myReceipts = useSelector((state) => state.myReceipts)
  const { receipts, loading, pages, page, count } = myReceipts

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const countRcpt = useSelector((state) => state.countRcpt)
  const { countRecpt } = countRcpt

  const formatDate = (value) => {
    if (!value) return ''
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
      .format(new Date(value))
      .replace(/,/g, '')
  }

  const getReceiptImageSrc = (qfname) => {
    return `${API_URL}/receipts/image/${encodeURIComponent(qfname)}`
  }

  // Change handler
  function showAlert(message, variant = 'success', seconds) {
    setAlert({
      show: true,
      message,
      variant
    })

    setTimeout(() => {
      setAlert({
        show: false,
        message: '',
        variant: 'success'
      })
    }, seconds)
  }

  // Event handlers
  const deleteHandler = async (rimgid) => {
    if (!window.confirm('Are you sure?')) return

    const result = await dispatch(deleteReceipt(rimgid))

    if (result?.success) {
      await dispatch(getMyReceipts(pageNumber, keyword))
      showAlert('Receipt Deleted', 'success', 1000)
    } else {
      showAlert(result?.error || 'Failed to delete receipt', 'danger', 1500)
    }
  }

  const editHandler = (id) => {
    dispatch(getReceipt(id))
    navigate(`/receipts/edit/${id}`)
  }

  const selectHandler = (rimgid) => {
    if (selectedReceiptIdsRef.current.has(rimgid)) {
      selectedReceiptIdsRef.current.delete(rimgid)
    } else {
      selectedReceiptIdsRef.current.add(rimgid)
    }

    setSelectedCount(selectedReceiptIdsRef.current.size)
  }

  const allOnPageSelected =
    receipts &&
    receipts.length > 0 &&
    receipts.every((receipt) =>
      selectedReceiptIdsRef.current.has(receipt.rimgid)
    )

  const selectAllOnPageHandler = () => {
    if (!receipts || receipts.length === 0) return

    if (allOnPageSelected) {
      receipts.forEach((receipt) => {
        selectedReceiptIdsRef.current.delete(receipt.rimgid)
      })
    } else {
      receipts.forEach((receipt) => {
        selectedReceiptIdsRef.current.add(receipt.rimgid)
      })
    }

    setSelectedCount(selectedReceiptIdsRef.current.size)
  }

  const escapePrintHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  const deleteMultiHandler = async () => {
    if (!window.confirm('Are you sure?')) return

    const idsToDelete = Array.from(selectedReceiptIdsRef.current)

    if (idsToDelete.length === 0) return

    const results = await Promise.all(
      idsToDelete.map((id) => dispatch(deleteReceipt(id)))
    )

    const failedCount = results.filter((result) => !result?.success).length
    const deletedCount = idsToDelete.length - failedCount

    selectedReceiptIdsRef.current.clear()
    setSelectedCount(0)

    if (deletedCount > 0) {
      await dispatch(getMyReceipts(pageNumber, keyword))
    }

    if (failedCount === 0) {
      showAlert(`${deletedCount} Receipts Deleted`, 'success', 1200)
    } else {
      showAlert(
        `${deletedCount} deleted, ${failedCount} failed`,
        'warning',
        1800
      )
    }
  }

  const printHandler = (receipt) => {
    const printWindow = window.open('', '_blank')
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${formatDate(receipt.createdAt)}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background-color: #f5f5f5;
            }
            .print-container {
              max-width: 600px;
              margin: 0 auto;
              background-color: white;
              padding: 30px;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            }
            .receipt-header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .receipt-title {
              font-size: 24px;
              font-weight: bold;
              margin: 0;
              color: #333;
            }
            .receipt-date {
              font-size: 14px;
              color: #666;
              margin: 5px 0 0 0;
            }
            .receipt-category {
              display: inline-block;
              background-color: #007bff;
              color: white;
              padding: 4px 12px;
              border-radius: 20px;
              font-size: 12px;
              margin-top: 10px;
            }
            .receipt-body {
              margin: 20px 0;
            }
            .receipt-image {
              width: 100%;
              max-height: 500px;
              object-fit: contain;
              border: 1px solid #ddd;
              border-radius: 4px;
              margin: 15px 0;
            }
            .receipt-text {
              font-size: 14px;
              color: #333;
              line-height: 1.6;
              white-space: pre-wrap;
              word-wrap: break-word;
              background-color: #f9f9f9;
              padding: 15px;
              border-radius: 4px;
              border-left: 4px solid #007bff;
            }
            .receipt-footer {
              text-align: center;
              font-size: 12px;
              color: #999;
              border-top: 1px solid #ddd;
              padding-top: 15px;
              margin-top: 20px;
            }
            @media print {
              body {
                background-color: white;
                margin: 0;
                padding: 0;
              }
              .print-container {
                box-shadow: none;
                max-width: 100%;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="receipt-header">
              <h1 class="receipt-title">Receipt</h1>
              <p class="receipt-date">${formatDate(receipt.createdAt)}</p>
              <span class="receipt-category">${receipt.rcategory}</span>
            </div>
            <div class="receipt-body">
              <img src="${getReceiptImageSrc(receipt.qfname)}" alt="Receipt" class="receipt-image" onerror="this.style.display='none'">
              <div class="receipt-text">${receipt.rcptext}</div>
            </div>
            <div class="receipt-footer">
              <p>Printed on ${new Date().toLocaleString()}</p>
              <p>microreceipt.com</p>
            </div>
          </div>
        </body>
      </html>
    `
    printWindow.document.write(printContent)
    printWindow.document.close()
    setTimeout(() => printWindow.print(), 250)
  }

  const printMultiHandler = async () => {
    const idsToPrint = Array.from(selectedReceiptIdsRef.current)
    if (idsToPrint.length === 0) return

    const receiptsToPrint = receipts.filter((r) =>
      idsToPrint.includes(r.rimgid)
    )
    const compact = printMode === 'compact'

    const printWindow = window.open('', '_blank')
    const receiptCards = receiptsToPrint
      .map(
        (receipt) => `
        <div class="receipt-card">
          <div class="card-header">
            <span class="receipt-category">${escapePrintHtml(receipt.rcategory)}</span>
            <span class="receipt-date">${formatDate(receipt.createdAt)}</span>
          </div>
          <img
            src="${getReceiptImageSrc(receipt.qfname)}"
            alt="Receipt"
            class="receipt-image"
            onerror="this.style.display='none'"
          />
          <div class="receipt-text">${escapePrintHtml(receipt.rcptext)}</div>
        </div>
      `
      )
      .join('')

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipts - ${new Date().toLocaleDateString()}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body {
              font-family: Arial, sans-serif;
              background: white;
              padding: 16px;
              color: #333;
            }
            .page-header {
              text-align: center;
              padding-bottom: 12px;
              margin-bottom: 16px;
              border-bottom: 2px solid #333;
            }
            .page-header h1 {
              font-size: 18px;
              font-weight: bold;
            }
            .page-header p {
              font-size: 11px;
              color: #666;
              margin-top: 4px;
            }
            .receipt-grid {
              display: grid;
              grid-template-columns: ${compact ? '1fr 1fr' : '1fr'};
              gap: ${compact ? '14px' : '12px'};
            }
            .receipt-card {
              border: 1px solid #ccc;
              border-radius: 6px;
              padding: ${compact ? '10px' : '14px'};
              break-inside: avoid;
              page-break-inside: avoid;
              background: #fff;
            }
            .card-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 8px;
              padding-bottom: 6px;
              border-bottom: 1px solid #eee;
            }
            .receipt-category {
              background-color: #007bff;
              color: white;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 10px;
              font-weight: bold;
            }
            .receipt-date {
              font-size: 10px;
              color: #666;
            }
            .receipt-image {
              width: 100%;
              max-height: ${compact ? '160px' : '280px'};
              object-fit: contain;
              border: 1px solid #eee;
              border-radius: 4px;
              margin: 6px 0;
            }
            .receipt-text {
              font-size: ${compact ? '11px' : '12px'};
              color: #444;
              line-height: 1.5;
              white-space: pre-wrap;
              word-wrap: break-word;
              max-height: ${compact ? '80px' : 'none'};
              overflow: ${compact ? 'hidden' : 'visible'};
              border-left: 3px solid #007bff;
              padding-left: 6px;
              margin-top: 4px;
            }
            .page-footer {
              text-align: center;
              font-size: 10px;
              color: #aaa;
              margin-top: 16px;
              padding-top: 10px;
              border-top: 1px solid #eee;
            }
            @media print {
              body { padding: 8px; }
              .receipt-grid { gap: 10px; }
            }
          </style>
        </head>
        <body>
          <div class="page-header">
            <h1>Receipts (${receiptsToPrint.length})</h1>
            <p>Printed on ${new Date().toLocaleString()} &mdash; ${compact ? 'Compact' : 'Detailed'} mode &mdash; microreceipt.com</p>
          </div>
          <div class="receipt-grid">
            ${receiptCards}
          </div>
          <div class="page-footer">
            microreceipt.com &mdash; ${receiptsToPrint.length} receipt(s)
          </div>
        </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
    selectedReceiptIdsRef.current.clear()
    setSelectedCount(0)
    showAlert(
      `Printed ${receiptsToPrint.length} receipt(s). Selection cleared.`,
      'info',
      1600
    )
    setTimeout(() => printWindow.print(), 250)
  }

  useEffect(() => {
    if (userInfo && countRecpt > 0) {
      dispatch(getMyReceipts(pageNumber, keyword))
      dispatch({ type: CATEGORY_RECEIPTS_RESET })
    } else {
      navigate('/dashboard')
    }
  }, [dispatch, navigate, userInfo, keyword, countRecpt, pageNumber])

  useEffect(() => {
    if (count === undefined && notfound === true) {
      showAlert('No Receipts Found', 'warning', 1500)
      setNotfound(true)
      navigate('/receipts')
    }
  }, [navigate, count, alert, notfound])

  return (
    <Container
      className='receipt-screen my-2'
      fluid
      style={{
        '--rc-bg': theme.bg,
        '--rc-card': theme.card,
        '--rc-text': theme.text,
        '--rc-subtext': theme.subtext,
        '--rc-accent': theme.accent,
        '--rc-border': theme.sidebarAccent,
        '--rc-muted': theme.surfaceMuted,
        '--rc-danger': theme.danger
      }}
    >
      <div className='receipt-hero'>
        <h2 className='receipt-hero-title'>My Receipts</h2>
        <p className='receipt-hero-subtitle'>
          Browse, edit, and organize your uploaded receipts.
        </p>
        <div className='receipt-hero-meta'>
          <span>{count || 0} total</span>
          {' | '}
          <span>{selectedCount} selected</span>
        </div>
      </div>
      {alert.show && <Alert variant={alert.variant}>{alert.message}</Alert>}
      {loading && <Loader />}
      {receipts && !loading && <Paginate pages={pages} page={page} />}

      <Row className='g-4'>
        {!loading && receipts && receipts.length === 0 && (
          <Col xs={12} className='text-center py-5'>
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🧾</div>
            <h5 style={{ color: theme.text }}>No receipts yet</h5>
            <p
              style={{
                color: theme.subtext,
                maxWidth: '360px',
                margin: '0 auto 1rem'
              }}
            >
              Share your QR code with a merchant and your digital receipts will
              appear here automatically.
            </p>
            <Button
              variant='outline-primary'
              onClick={() => navigate('/profile')}
              style={{
                borderColor: theme.accent,
                color: theme.accent,
                backgroundColor: 'transparent',
                border: `1px solid ${theme.accent}`
              }}
            >
              View My QR Code
            </Button>
          </Col>
        )}
        {!loading &&
          receipts &&
          receipts.map((receipt) => (
            <Col key={receipt._id} sm={12} md={6} lg={4} xl={3}>
              <Card
                className='receipt-card h-100'
                style={{
                  backgroundColor: theme.card,
                  color: theme.text,
                  borderColor: theme.sidebarAccent,
                  borderRadius: '16px'
                }}
              >
                <Card.Header
                  className='receipt-card-header'
                  style={{
                    backgroundColor: theme.surfaceMuted,
                    color: theme.subtext
                  }}
                >
                  <Button
                    className='btn btn-sm receipt-category-btn'
                    variant='outline-primary'
                    onClick={() =>
                      navigate(`/category`, {
                        state: { category: receipt.rcategory }
                      })
                    }
                    style={{
                      borderColor: theme.accent,
                      color: theme.accent,
                      backgroundColor: 'transparent'
                    }}
                  >
                    {receipt.rcategory} <i className='fas fa-tag'></i>
                  </Button>
                </Card.Header>

                <Card.Body>
                  <div className='receipt-image-shell'>
                    {imageErrors[receipt.rimgid] ? (
                      <div className='receipt-image-fallback'>
                        Image unavailable
                      </div>
                    ) : (
                      <Card.Img
                        className='receipt-image'
                        src={getReceiptImageSrc(receipt.qfname)}
                        loading='lazy'
                        variant='top'
                        alt={receipt.rcptext}
                        onError={() =>
                          setImageErrors((prev) => ({
                            ...prev,
                            [receipt.rimgid]: true
                          }))
                        }
                      />
                    )}
                  </div>
                  <Link
                    to={`/receipts/${receipt.rimgid}`}
                    className='receipt-title-link'
                    style={{ color: theme.text }}
                  >
                    <Card.Title className='receipt-date' as='div'>
                      {formatDate(receipt.createdAt)}
                    </Card.Title>
                  </Link>
                  <Card.Text className='receipt-text' as='div'>
                    {receipt.rcptext}
                  </Card.Text>
                </Card.Body>

                <Card.Footer
                  className='receipt-card-footer'
                  style={{
                    backgroundColor: theme.surfaceMuted,
                    borderColor: theme.sidebarAccent
                  }}
                >
                  <Button
                    className='receipt-action-btn'
                    variant='outline-primary'
                    block='true'
                    onClick={() => editHandler(receipt._id)}
                    style={{
                      borderColor: theme.accent,
                      color: theme.accent,
                      backgroundColor: 'transparent'
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>{' '}
                  <Button
                    className='receipt-action-btn'
                    variant='outline-info'
                    block='true'
                    onClick={() => printHandler(receipt)}
                    style={{
                      borderColor: '#17a2b8',
                      color: '#17a2b8',
                      backgroundColor: 'transparent'
                    }}
                    title='Print receipt'
                  >
                    <FontAwesomeIcon icon={faPrint} />
                  </Button>{' '}
                  <Button
                    className='receipt-action-btn'
                    variant='outline-danger'
                    block='true'
                    onClick={() => deleteHandler(receipt.rimgid)}
                    style={{
                      borderColor: theme.danger,
                      color: theme.danger,
                      backgroundColor: 'transparent'
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                  <Form.Check
                    className='receipt-select mt-2'
                    type='checkbox'
                    label='Select'
                    checked={selectedReceiptIdsRef.current.has(receipt.rimgid)}
                    onChange={() => selectHandler(receipt.rimgid)}
                  />
                </Card.Footer>
              </Card>
            </Col>
          ))}
      </Row>
      {selectedCount > 0 && (
        <Row>
          <Col className='d-flex flex-wrap justify-content-center align-items-center gap-2'>
            <Form.Check
              className='receipt-select'
              type='checkbox'
              label='Select all on page'
              checked={allOnPageSelected}
              onChange={selectAllOnPageHandler}
            />
            <Form.Select
              size='sm'
              value={printMode}
              onChange={(e) => setPrintMode(e.target.value)}
              style={{
                maxWidth: '170px',
                borderColor: theme.sidebarAccent,
                backgroundColor: theme.card,
                color: theme.text
              }}
            >
              <option value='compact'>Compact print</option>
              <option value='detailed'>Detailed print</option>
            </Form.Select>
            <Button
              className='btn btn-md btn-info my-3 px-4'
              onClick={() => printMultiHandler()}
            >
              <i className='fas fa-print'></i> Print {selectedCount} Selected
            </Button>
            <Button
              className='btn btn-md btn-danger my-3 px-4'
              onClick={() => deleteMultiHandler()}
            >
              <i className='fas fa-trash'></i> Delete {selectedCount} Selected
            </Button>
          </Col>
        </Row>
      )}
    </Container>
  )
}

export default ReceiptScreen
